import { Alert, Button, Card, Checkbox, Typography } from '@sonnen/web-ui';
import { parseHtmlTags } from '@sonnen/web-utils';
import { useState } from 'react';
import { useEffect } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import Banner from '../../assets/images/optimization_info_banner.webp';
import { CurrentUserSiteOptionsCreateContractFlow } from '../../graphql/generated';
import { useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { ModuleName } from '../../types';
import { hasSonnenStromContract, moduleAvailable } from '../../utils/utils';
import { BatteryOptimizationOverlay } from '../BatteryOptimizationOverlay/BatteryOptimizationOverlay';
import LoginPromptCard from '../LoginPromptCard/LoginPromptCard';
import { ModuleCard } from '../ModuleCard/ModuleCard';
import styles from './OptimizationInfoCard.module.css';

const OptimizationInfoCard = () => {
  const { t } = useTranslation();
  const { user, setFormValues, formValues, selectedSite } = useAppContext();
  const { offer } = useOfferContext();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const available = moduleAvailable(offer, ModuleName.PRICE_OPTIMIZATION);
  const incompatible = user && !available && !hasSonnenStromContract(selectedSite);
  const { interestedInHardwareOffer } = formValues;

  const contentKey = incompatible
    ? 'optimizationInfoCard.incompatible'
    : !user
      ? 'optimizationInfoCard.visitor'
      : 'optimizationInfoCard.customer';

  if (!offer || !offer.energyDynamic) return false;

  const { siteHasCharger, configurationWithBatteryOptimizationModule } = offer.energyDynamic;

  const openOverlay = () => setOverlayOpen(true);

  const closeOverlay = () => setOverlayOpen(false);

  const showCheckBox =
    selectedSite?.options?.createContractFlow ===
      CurrentUserSiteOptionsCreateContractFlow.SwitchFromSonnenStromContract || !user;

  const onChange = () => {
    setFormValues({
      ...formValues,
      interestedInHardwareOffer: !interestedInHardwareOffer,
    });
  };

  useEffect(() => {
    document.body.style.overflow = overlayOpen ? 'hidden' : 'unset';
    if (!overlayOpen) return;

    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') setOverlayOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [overlayOpen]);

  return (
    <Card
      paddingSize="none"
      dataTestId="optimization-info-card"
      data-testid="optimization-info-card"
    >
      <img src={Banner} alt="optimization-card-banner" />
      <div className={styles.content}>
        <Typography.H2>{t(`${contentKey}.title`)}</Typography.H2>
        <Typography.Body1 variant="secondary">{t(`${contentKey}.body`)}</Typography.Body1>

        {user && (
          <Typography.Body1 variant="secondary">
            <div className={styles.inline}>
              {t('optimizationInfoCard.customer.moreInfo1')}
              <Button
                dataTestId="optimization-overlay-btn"
                label={t('optimizationInfoCard.customer.moreInfo2')}
                size="medium"
                type="button"
                variant="quaternary"
                onClick={openOverlay}
              />
            </div>
          </Typography.Body1>
        )}
        {siteHasCharger && configurationWithBatteryOptimizationModule && (
          <Alert
            title={t(`modules.priceOptimizationSoonAvailableForChargers.title`)}
            variant="info"
          >
            {parseHtmlTags(t(`modules.priceOptimizationSoonAvailableForChargers.content`))}
          </Alert>
        )}

        {available && <ModuleCard name={ModuleName.PRICE_OPTIMIZATION} />}
        {showCheckBox && (
          <Checkbox
            name="hardware-offer"
            dataTestId="hardware-offer"
            checked={interestedInHardwareOffer}
            clickableLabel
            onChange={onChange}
          >
            <Typography.Button1 variant="primary">
              {t('home.priceOptimizationTeaserCard.checkbox')}
            </Typography.Button1>
          </Checkbox>
        )}
        {!user && <LoginPromptCard i18nScope="modules.priceOptimization" />}
      </div>
      {overlayOpen && <BatteryOptimizationOverlay onClose={closeOverlay} />}
    </Card>
  );
};

export default OptimizationInfoCard;
